import { MessageCircle, Send, Link2, AtSign, Search, ArrowUpRight } from "lucide-react";

/* ---------------- tone presets (data-driven) ---------------- */
export const TONES = {
  cinematic_noir: {
    bg: "linear-gradient(160deg,#0a0a0a 0%,#050505 100%)",
    panel: "rgba(0,0,0,0.55)",
    border: "rgba(255,255,255,0.14)",
    text: "#F5F5F5",
    sub: "#a8a8a8",
    accent: "#FF2A2A",
    mono: false,
  },
  deep_blue: {
    bg: "linear-gradient(165deg,#0a1730 0%,#04070f 70%)",
    panel: "rgba(6,16,34,0.55)",
    border: "rgba(120,160,255,0.18)",
    text: "#EAF0FF",
    sub: "#8fa3c8",
    accent: "#FF2A2A",
    mono: false,
  },
  crimson_minimal: {
    bg: "linear-gradient(160deg,#0b0505 0%,#050202 100%)",
    panel: "rgba(20,4,4,0.5)",
    border: "rgba(255,42,42,0.35)",
    text: "#FFFFFF",
    sub: "#c98a8a",
    accent: "#FF2A2A",
    mono: true,
  },
  ghost: {
    bg: "linear-gradient(160deg,rgba(5,5,5,0.35) 0%,rgba(5,5,5,0.55) 100%)",
    panel: "transparent",
    border: "rgba(255,255,255,0.22)",
    text: "#FFFFFF",
    sub: "rgba(255,255,255,0.6)",
    accent: "#FF6a6a",
    mono: true,
  },
};

const ICONS = {
  comment_keyword: MessageCircle,
  dm_keyword: Send,
  story_link: Link2,
  profile_link: AtSign,
  search_profile: Search,
  direct_link: ArrowUpRight,
};

/**
 * Reusable, data-driven FANLY Trigger overlay for 9:16 video.
 * Non-clickable (pointer-events none). Scales with its container via cqw units.
 * config: { project_title, supporting_line, instruction_line, trigger_type, brand_signature, tone_preset, variant }
 */
export default function TriggerOverlay({ config = {}, transparent = false }) {
  const t = TONES[config.tone_preset] || TONES.cinematic_noir;
  const variant = config.variant || "end_card";
  const Icon = ICONS[config.trigger_type];
  const mono = t.mono ? "'JetBrains Mono', monospace" : undefined;

  const root = {
    position: "absolute",
    inset: 0,
    containerType: "size",
    overflow: "hidden",
    pointerEvents: "none",
    background: transparent ? "transparent" : t.bg,
    color: t.text,
  };

  const Brand = ({ style }) => (
    <div style={{ display: "flex", alignItems: "center", gap: "1.5cqw", ...style }}>
      <span style={{ width: "1.6cqw", height: "1.6cqw", borderRadius: "50%", background: t.accent,
        boxShadow: `0 0 3cqw ${t.accent}` }} />
      <span style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: "3cqw",
        letterSpacing: "0.4em", textTransform: "uppercase", color: t.sub }}>
        {config.brand_signature || "FANLY"}
      </span>
    </div>
  );

  const Pill = ({ delay = 0 }) =>
    config.instruction_line ? (
      <div
        style={{
          display: "inline-flex", alignItems: "center", gap: "2.5cqw",
          padding: "3cqw 5cqw", borderRadius: "999px", opacity: 1,
          border: `1px solid ${t.border}`, background: t.panel === "transparent" ? "rgba(0,0,0,0.35)" : t.panel,
          backdropFilter: "blur(6px)",
        }}
      >
        {Icon && <Icon style={{ width: "4.5cqw", height: "4.5cqw", color: t.accent }} />}
        <span style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: "4cqw",
          letterSpacing: "0.12em", color: t.text }}>
          {config.instruction_line}
        </span>
      </div>
    ) : null;

  /* ---------------- END TRIGGER CARD ---------------- */
  if (variant === "end_card") {
    return (
      <div style={{ ...root, animation: "fadeUp 0.8s ease both" }}>
        <div style={{ position: "absolute", inset: 0,
          background: `radial-gradient(60% 40% at 50% 42%, ${t.accent}22 0%, transparent 60%)` }} />
        <div style={{ position: "absolute", inset: 0, display: "flex", flexDirection: "column",
          alignItems: "center", justifyContent: "center", textAlign: "center", padding: "8cqw" }}>
          {config.supporting_line && (
            <p style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: "3.4cqw",
              letterSpacing: "0.45em", textTransform: "uppercase", color: t.accent, margin: 0, opacity: 1 }}>
              {config.supporting_line}
            </p>
          )}
          <h1 style={{ fontFamily: mono || "'Unbounded', sans-serif", fontWeight: 800, opacity: 1,
            fontSize: "15cqw", lineHeight: 0.95, margin: "3cqw 0 6cqw", textTransform: "uppercase",
            letterSpacing: "-0.02em", color: t.text }}>
            {config.project_title}
          </h1>
          {config.instruction_line && (
            <div style={{
              display: "inline-flex", alignItems: "center", gap: "2.5cqw",
              padding: "3.2cqw 5.5cqw", borderRadius: "999px", opacity: 1,
              border: `1px solid ${t.border}`,
              background: t.panel === "transparent" ? "rgba(0,0,0,0.4)" : t.panel,
              backdropFilter: "blur(6px)",
            }}>
              {Icon && <Icon style={{ width: "4.5cqw", height: "4.5cqw", color: t.accent }} />}
              <span style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: "4cqw",
                letterSpacing: "0.12em", color: t.text }}>
                {config.instruction_line}
              </span>
            </div>
          )}
        </div>
        <Brand style={{ position: "absolute", bottom: "7cqw", left: 0, right: 0, justifyContent: "center" }} />
      </div>
    );
  }

  /* ---------------- MID-VIDEO SIGNATURE ---------------- */
  if (variant === "mid_signature") {
    return (
      <div style={{ ...root, background: transparent ? "transparent" : "transparent", animation: "fadeUp 0.8s ease both" }}>
        <div style={{ position: "absolute", left: "6cqw", bottom: "10cqw" }}>
          <div style={{ height: "0.5cqw", width: "12cqw", background: t.accent, marginBottom: "2.5cqw",
            borderRadius: 2 }} />
          <p style={{ fontFamily: mono || "'Unbounded', sans-serif", fontWeight: 700,
            fontSize: "6cqw", margin: 0, textTransform: "uppercase", letterSpacing: "-0.01em",
            textShadow: "0 2px 20px rgba(0,0,0,0.6)" }}>
            {config.project_title}
          </p>
          {config.supporting_line && (
            <p style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: "3cqw",
              letterSpacing: "0.2em", color: t.sub, margin: "1.5cqw 0 0", textTransform: "lowercase" }}>
              {config.supporting_line}
            </p>
          )}
        </div>
        <Brand style={{ position: "absolute", top: "6cqw", right: "6cqw" }} />
      </div>
    );
  }

  /* ---------------- SOFT CTA PANEL ---------------- */
  return (
    <div style={{ ...root, background: transparent ? "transparent" : "transparent", animation: "fadeUp 0.8s ease both" }}>
      <div style={{ position: "absolute", left: 0, right: 0, bottom: 0, height: "45%",
        background: "linear-gradient(to top, rgba(0,0,0,0.85), transparent)" }} />
      <div
        style={{ position: "absolute", left: "5cqw", right: "5cqw", bottom: "7cqw",
          borderRadius: "5cqw", border: `1px solid ${t.border}`,
          background: t.panel === "transparent" ? "rgba(0,0,0,0.4)" : t.panel,
          backdropFilter: "blur(10px)", padding: "6cqw" }}
      >
        {config.supporting_line && (
          <p style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: "3.2cqw",
            letterSpacing: "0.35em", textTransform: "uppercase", color: t.accent, margin: "0 0 2cqw" }}>
            {config.supporting_line}
          </p>
        )}
        <h2 style={{ fontFamily: mono || "'Unbounded', sans-serif", fontWeight: 700,
          fontSize: "8cqw", margin: "0 0 4cqw", lineHeight: 1, textTransform: "uppercase" }}>
          {config.project_title}
        </h2>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: "3cqw" }}>
          <Pill />
          <Brand />
        </div>
      </div>
    </div>
  );
}
